import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ColorCircularProgress, SvgIcon, TextField, Typography, OnlineIndicator, Tooltip } from 'components'
import { makeStyles } from '@material-ui/core/styles'
import { colors } from 'constants/index'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import { IconButton, InputAdornment } from '@material-ui/core'
import { ReactComponent as SearchIcon } from 'static/chat/icon_24_ search.svg'
import { ReactComponent as CloseIcon } from 'static/chat/_close.svg'
import { ReactComponent as ShareIcon } from 'static/cases/share.svg'
import { getFormattedMessageDate } from 'utils/getters/common'
import SharedClientProjectsModal from 'components/connections/SharedClientProjectsModal'
import {
  getUserChats,
  increaseChatListOffset,
  resetChatListOffset,
  setChatInfo,
  setChatListSearchValue,
  setSelectedChat,
  setChatsType,
} from 'store/actions/chat/chat'
import classNames from 'classnames'

const useStyles = makeStyles({
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 59,
    borderBottom: `1px solid ${colors.BASIC.COLOR['10']}`,
  },
  title: {
    margin: '18px 24px 11px 24px',
    fontWeight: 500,
    fontSize: 16,
  },
  tabsContainer: {
    display: 'flex',
    borderBottom: `1px solid ${colors.BASIC.COLOR['10']}`,
    padding: '0 16px',
    gap: 8,
  },
  tabHeader: {
    padding: '14px 16px 12px 16px',
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '18px',
    color: colors.BASIC.COLOR['50'],
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
    position: 'relative',
    textTransform: 'none',
    '&:hover': {
      color: colors.BASIC.COLOR['80'],
    },
  },
  activeTab: {
    color: colors.BASIC.COLOR['80'],
    borderBottom: `2px solid ${colors.BASIC.COLOR['80']}`,
  },
  tabIndicator: {
    position: 'absolute',
    top: '50%',
    right: 8,
    transform: 'translateY(-50%)',
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: colors.ERROR,
  },
  search: {
    backgroundColor: colors.BASIC.COLOR['5'],
    borderRadius: 0,
    height: 42,
    fontSize: 15,
  },
  searchFocused: {
    backgroundColor: colors.WHITE,
    '& $icon': {
      fill: colors.PRIMARY.COLOR['50'],
    },
    '& $clearButton': {
      backgroundColor: colors.WHITE,
    },
    '& $searchButton': {
      backgroundColor: colors.WHITE,
    },
  },
  searchIcon: {
    height: 24,
    width: 24,
  },
  classesTabIndicator: {
    backgroundColor: colors.PRIMARY.COLOR['40'],
    left: 0,
    width: 3,
  },
  tabs: {
    backgroundColor: colors.BASIC.COLOR['0'],
    flexGrow: 1,
  },
  tab: {
    maxWidth: '100%',
    padding: 0,
    opacity: 1,
  },
  selectedTab: {
    backgroundColor: colors.WHITE,
    color: colors.PRIMARY.COLOR['40'],
    fontWeight: 500,
  },
  tabInnerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
    padding: '14px 28px 14px 16px',
  },
  tabInnerContainerLoadMore: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    padding: '14px 28px 14px 16px',
    color: colors.PRIMARY.COLOR['50'],
  },
  unreadMessages: {
    backgroundColor: colors.ERROR,
    color: colors.WHITE,
    borderRadius: 12,
    padding: '1px 7px',
    marginRight: 8,
    fontSize: 11,
    fontWeight: 400,
  },
  indicators: {
    display: 'flex',
    alignItems: 'center',
  },
  tabTime: {
    color: colors.BASIC.COLOR['100'],
    fontWeight: 400,
  },
  clearButton: {
    height: 30,
    width: 30,
    backgroundColor: colors.BASIC.COLOR['5'],
  },
  searchButton: {
    height: 30,
    width: 30,
    padding: 0,
    backgroundColor: colors.BASIC.COLOR['5'],
  },
  adornedEnd: {
    position: 'relative',
    marginRight: 35,
  },
  icon: {
    height: 24,
    width: 24,
  },
  circleClass: {
    top: -15,
    left: -15,
  },
  chatMemberName: {
    textTransform: 'none',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  indicator: {
    marginRight: 6,
  },
  indicatorWrapper: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: '60%',
  },
  transparent: {
    opacity: 0,
  },
  shareButton: {
    width: 16,
    height: 16,
    marginLeft: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    cursor: 'pointer',
  },
  shareButtonRotated: {
    transform: 'rotate(180deg)',
  },
  tooltip: {
    backgroundColor: colors.BASIC.COLOR[80],
    color: colors.WHITE,
    fontSize: 13,
    fontWeight: 'normal',
  },
})

const ChatRow = ({ chat, chatsType, onShareIconClick }) => {
  const classes = useStyles()

  // Determine if chat is shared (for clients)
  const isSharedByMe = chatsType === 'client' && (chat.shared_cases_count > 0 || chat.shared_with_count > 0)
  const isSharedWithMe = chatsType === 'client' && !!chat.shared_by_full_name?.length

  // Determine if chat is shared (for colleagues)
  const sharedCases = chatsType === 'colleague' ? chat.shared_case_names || [] : []
  const hasSharedCases = sharedCases.length > 0

  const getShareTooltip = () => {
    if (chatsType === 'client') {
      if (isSharedByMe) {
        const count = chat.shared_cases_count || chat.shared_with_count || 0
        return `${count} project${count !== 1 ? 's' : ''} shared with other providers for this client.`
      } else if (isSharedWithMe && chat.shared_by_full_name) {
        return `This client's projects shared with you by ${chat.shared_by_full_name}`
      }
    } else if (chatsType === 'colleague' && hasSharedCases) {
      const caseNames = sharedCases.join(', ')
      return `Added as part of ${caseNames} project${sharedCases.length !== 1 ? 's' : ''}`
    }
    return ''
  }

  const showShareIcon =
    (chatsType === 'client' && (isSharedByMe || isSharedWithMe)) || (chatsType === 'colleague' && hasSharedCases)

  const handleShareIconClick = e => {
    e.stopPropagation()
    if (chatsType === 'client' && onShareIconClick) {
      onShareIconClick(chat, isSharedByMe, isSharedWithMe)
    }
  }

  return (
    <>
      <div className={classes.indicatorWrapper}>
        <OnlineIndicator
          className={classNames({ [classes.indicator]: true, [classes.transparent]: !chat.chat_member_is_online })}
        />
        <span className={classes.chatMemberName}>{chat.chat_member_full_name}</span>
        {showShareIcon && (
          <Tooltip tooltipClass={classes.tooltip} title={getShareTooltip()} placement="bottom-start">
            <ShareIcon
              className={`${classes.shareButton} ${isSharedWithMe ? classes.shareButtonRotated : ''}`}
              onClick={handleShareIconClick}
            />
          </Tooltip>
        )}
      </div>

      <div className={classes.indicators}>
        {!!chat.unread_messages_count && <div className={classes.unreadMessages}>{chat.unread_messages_count}</div>}
        <span className={classes.tabTime}>{getFormattedMessageDate(chat.last_message_at)}</span>
      </div>
    </>
  )
}

const ProAccountChatListContainer = props => {
  const { selectedChatId } = props
  const classes = useStyles()
  const dispatch = useDispatch()
  const chats = useSelector(state => state.chat.chats)
  const chatCount = useSelector(state => state.chat.count)
  const chatsAreLoading = useSelector(state => state.chat.chatsAreLoading)
  const searchValue = useSelector(state => state.chat.searchValue)
  const chatsType = useSelector(state => state.chat.chatsType)
  const [showSharedProjectsModal, setShowSharedProjectsModal] = useState(false)
  const [selectedClientForShare, setSelectedClientForShare] = useState(null)
  const currentUser = useSelector(state => state.auth.currentUser)
  const isAccountant = currentUser?.subrole === 'accountant'

  const onChatSelect = (event, newValue) => {
    if (newValue === 'load_more') {
      onLoadMore()
    }
    const selectedChat = chats.find(chat => chat.connection_uuid === newValue)
    if (selectedChat) {
      dispatch(setSelectedChat(selectedChat))
      dispatch(setChatInfo(selectedChat.connection_uuid, { unread_messages_count: 0 }))
    }
  }

  const onLoadMore = () => {
    dispatch(increaseChatListOffset())
    dispatch(getUserChats(false))
  }

  const onClearSearch = () => {
    dispatch(setChatListSearchValue(''))
    dispatch(resetChatListOffset())
    dispatch(getUserChats())
  }

  const onKeyPress = e => {
    if (e.keyCode === 13) {
      onSearch()
    }
  }

  const onSearch = () => {
    dispatch(resetChatListOffset())
    dispatch(getUserChats())
  }

  const handleTabChange = type => {
    dispatch(setChatsType(type))
    dispatch(resetChatListOffset())
    dispatch(getUserChats())
  }

  const handleShareIconClick = (chat, isSharedByMe, isSharedWithMe) => {
    setSelectedClientForShare({
      clientUuid: chat.client_uuid,
      clientName: chat.chat_member_full_name,
      type: isSharedByMe ? 'shared_by_me' : 'shared_with_me',
      proUuid: isSharedWithMe ? chat.main_pro_uuid : undefined,
    })
    setShowSharedProjectsModal(true)
  }

  return (
    <div className={classes.container}>
      <div className={classes.titleContainer}>
        <Typography variant="h4" className={classes.title}>
          Connections
        </Typography>
      </div>
      {isAccountant ? (
        <div className={classes.tabsContainer}>
          <div
            className={classNames(classes.tabHeader, { [classes.activeTab]: chatsType === 'client' })}
            onClick={() => handleTabChange('client')}
          >
            Clients
          </div>
          <div
            className={classNames(classes.tabHeader, { [classes.activeTab]: chatsType === 'colleague' })}
            onClick={() => handleTabChange('colleague')}
          >
            Colleagues
          </div>
        </div>
      ) : (
        <></>
      )}
      <TextField
        value={searchValue}
        placeholder="Search for people"
        onChange={e => {
          dispatch(setChatListSearchValue(e.target.value))
        }}
        onKeyDown={onKeyPress}
        variant="outlined"
        fullWidth
        InputProps={{
          classes: { root: classes.search, focused: classes.searchFocused },
          startAdornment: (
            <InputAdornment disableTypography className={classes.adornedStart} position="start">
              <IconButton onClick={onSearch} className={classes.searchButton}>
                <SvgIcon viewBox="0 0 24 24" className={classes.icon}>
                  <SearchIcon />
                </SvgIcon>
              </IconButton>
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment disableTypography className={classes.adornedEnd} position="end">
              {chatsAreLoading ? (
                <ColorCircularProgress size={30} positionClass={classes.circleClass} />
              ) : searchValue ? (
                <IconButton onClick={onClearSearch} className={classes.clearButton}>
                  <SvgIcon viewBox="0 0 24 24">
                    <CloseIcon />
                  </SvgIcon>
                </IconButton>
              ) : (
                ''
              )}
            </InputAdornment>
          ),
        }}
      />

      {chatsAreLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}></div>
      ) : (
        <Tabs
          value={selectedChatId || false}
          onChange={onChatSelect}
          className={classes.tabs}
          scrollable="true"
          variant="scrollable"
          orientation="vertical"
          classes={{
            indicator: classes.classesTabIndicator,
            root: classes.tabs,
          }}
        >
          {chats.map(chat => {
            return (
              <Tab
                key={chat.connection_uuid}
                value={chat.connection_uuid}
                label={<ChatRow chat={chat} chatsType={chatsType} onShareIconClick={handleShareIconClick} />}
                classes={{
                  root: classes.tab,
                  selected: classes.selectedTab,
                  wrapper: classes.tabInnerContainer,
                }}
              />
            )
          })}
          {chats.length < chatCount && !chatsAreLoading && (
            <Tab
              label="Load more"
              value={'load_more'}
              classes={{
                root: classes.tab,
                selected: classes.selectedTab,
                wrapper: classes.tabInnerContainerLoadMore,
              }}
            />
          )}
        </Tabs>
      )}
      <SharedClientProjectsModal
        isOpen={showSharedProjectsModal}
        onClose={() => {
          setShowSharedProjectsModal(false)
          setSelectedClientForShare(null)
        }}
        clientUuid={selectedClientForShare?.clientUuid}
        clientName={selectedClientForShare?.clientName}
        type={selectedClientForShare?.type}
        proUuid={selectedClientForShare?.proUuid}
      />
    </div>
  )
}

export default ProAccountChatListContainer
